<template>
  <div id="rolesComponent">
    <page-title :heading=heading :subheading=subheading :icon=icon></page-title>
    <v-container>
      <v-layout class="mb-2">
        <v-flex class="text-xs-right">
          <v-dialog persistent v-model="dialog" max-width="500px">
            <template v-slot:activator="{ on }">
              <v-btn
                color="primary"
                round
                outline
                v-on="on"
              >
                <v-icon left>add</v-icon>
                {{ $t('role.add') }}
              </v-btn>
            </template>
            <v-card>
              <v-toolbar flat>
                <h2 class="font-weight-black primary--text mb-0">{{formTitle}}</h2>
              </v-toolbar>
              <v-card-text>
                <v-container grid-list-xs>
                  <v-form
                    lazy-validation
                    ref="form"
                    v-model="valid"
                  >
                    <v-text-field
                      clearable
                      required
                      :rules="rules.textRules"
                      prepend-icon="edit"
                      :label="$t('general.name')"
                      v-model="editedItem.name"
                      :error-messages="errors.name"
                      @keyup.enter="save"
                    ></v-text-field>
                  </v-form>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary darken-1"
                  round
                  flat
                  @click="close"
                >{{ $t('general.cancel') }}</v-btn>
                <v-btn
                  :loading="loading"
                  color="primary"
                  round
                  depressed
                  @click="save"
                >{{ $t('general.save') }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-flex>
      </v-layout>
      <v-data-table
        class="elevation-2"
        :headers="headers"
        :items="roles"
        :rows-per-page-items="[10,15,25]"
        :pagination.sync="pagination"
      >
        <template v-slot:headers="props">
          <tr class="bg-heavy-rain">
            <th
              v-for="header in props.headers"
              :key="header.text"
              :class="['column', header.sortable ? 'sortable' : '', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
              @click="changeSort(header.value)"
            >
              <h2 class="font-weight-black primary--text mb-0">
                <v-icon
                  small
                  class="primary--text"
                  v-if="header.sortable"
                >arrow_upward</v-icon>
                {{ header.text }}
              </h2>
            </th>
          </tr>
        </template>
        <template v-slot:no-data>
          <v-btn flat large :loading="true"></v-btn>
        </template>
        <template v-slot:items="props">
          <tr>
            <td width="60px">{{props.item.id}}</td>
            <td class="text-sm-center">{{props.item.name}}</td>
            <td class="text-sm-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    round
                    outline
                    icon
                    small
                    color="primary"
                    @click="viewRol(props.item)"
                  >
                    <v-icon small>add</v-icon>
                  </v-btn>
                </template>
                <span>Ver mas</span>
              </v-tooltip>
            </td>
            <td width="200px">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    outline
                    icon
                    small
                    color="info"
                    v-on="on"
                    @click="editItem(props.item)"
                  >
                    <v-icon small>edit</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('role.edit') }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    outline
                    icon
                    small
                    color="error"
                    v-on="on"
                    @click="removed(props.item)"
                  >
                    <v-icon small>delete</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('general.delete') }}</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
      <v-dialog persistent v-model="removeDialog" max-width="500px">
        <v-card>
          <v-toolbar flat>
            <h2 class="font-weight-black error--text mb-0">{{ $t('role.delete') }}</h2>
          </v-toolbar>
          <v-card-text class="text-sm-justify">
            <p>{{ $t('general.deleteElementQuestion') }}</p>
            <p class="font-weight-black">
              <span class="error--text">{{ $t('general.name') }}: </span>
              {{removedItem.name}}
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="error darken-1"
              round
              flat
              @click="removeDialog = false"
            >{{ $t('general.cancel') }}</v-btn>
            <v-btn
              :loading="loading"
              color="error"
              round
              depressed
              @click="removeItem(removedItem)"
            >{{ $t('general.delete') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog persistent v-model="permissionsDialog" max-width="600px">
        <v-card>
          <v-toolbar flat v-if="permissionTitle === 'Adding'">
            <h2 class="font-weight-black primary--text mb-0">{{ $t('role.addPermission', {permission: permissionsItem.name}) }}</h2>
          </v-toolbar>
          <v-toolbar flat v-if="permissionTitle === 'Deleting'">
            <h2 class="font-weight-black error--text mb-0">{{ $t('role.removePermission', {permission: permissionsItem.name}) }}</h2>
          </v-toolbar>
          <v-card-text>
            <v-container>
              <v-form
                lazy-validation
                ref="formPermissions"
                v-model="validPermission"
              >
                <v-select
                  v-if="permissionTitle === 'Adding'"
                  required
                  :rules="rules.required"
                  v-model="permission"
                  :label="$t('permission.name')"
                  item-text="name"
                  item-value="name"
                  :items="permissionsHad"
                ></v-select>
                <v-select
                  v-if="permissionTitle === 'Deleting'"
                  required
                  :rules="rules.required"
                  v-model="permission"
                  :label="$t('permission.name')"
                  item-text="name"
                  item-value="id"
                  :items="permissionsItem.permissions"
                ></v-select>
              </v-form>
            </v-container>
          </v-card-text>
          <v-card-actions v-if="permissionTitle === 'Adding'">
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              round
              flat
              @click="closePermission"
            >{{ $t('general.cancel') }}</v-btn>
            <v-btn
              :loading="loading"
              :disabled="loading"
              color="primary"
              round
              depressed
              @click="savePermission(permissionsItem)"
            >{{ $t('general.add') }}</v-btn>
          </v-card-actions>
          <v-card-actions v-if="permissionTitle === 'Deleting'">
            <v-spacer></v-spacer>
            <v-btn
              color="error"
              round
              flat
              @click="closePermission"
            >{{ $t('general.cancel') }}</v-btn>
            <v-btn
              :loading="loading"
              :disabled="loading"
              color="error"
              round
              depressed
              @click="deletePermission(permissionsItem)"
            >{{ $t('general.remove') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog persistent v-model="viewDialog" width="600px">
        <v-card>
          <v-toolbar flat>
            <h2 class="font-weight-black primary--text mb-0">{{$t('role.name')}}: {{permissionsItem.name}}</h2>
          </v-toolbar>
          <v-card-text>
            <v-container grid-list-sm>
              <v-layout>
                <h2 class="primary--text">{{$t('general.permissions')}}: </h2>
              </v-layout>
              <v-layout row wrap>
                <p v-if="permissionsItem.permissions.length === 0">{{$t('role.noPermission')}}</p>
                <v-flex>
                  <v-data-table
                    v-if="permissionsItem.permissions.length >= 1"
                    flat
                    hide-actions
                    hide-headers
                    :items="permissionsItem.permissions"
                  >
                    <template v-slot:items="props">
                      <tr>
                        <td>{{props.item.id}}</td>
                        <td class="text-sm-left">{{props.item.name}}</td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              round
              flat
              @click="closeView"
            >{{$t('general.close')}}</v-btn>
            <v-btn
              round
              outline
              color="primary"
              @click="addPermissions(permissionsItem)"
            >{{$t('general.add')}}</v-btn>
            <v-btn
              round
              outline
              color="error"
              @click="removePermission(permissionsItem)"
            >{{$t('general.remove')}}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>
<script>
export default {
  name: 'rolesComponent',
  data () {
    return {
      pagination: {
        descending: false,
        page: 0,
        rowsPerPage: 15, // -1 for All",
        totalItems: 0
      },
      dialog: false,
      removeDialog: false,
      permissionsDialog: false,
      viewDialog: false,
      loading: false,
      valid: true,
      validPermission: true,
      editedIndex: -1,
      removedItem: {},
      permissionsItem: {permissions: [ ]},
      permission: null,
      permissionTitle: '',
      editedItem: {id: 0, name: '', guard_name: 'api', permissions: []},
      defaultItem: {id: 0, name: '', guard_name: 'api', permissions: []},
      roles: [],
      permissions: [],
      errors: {}
    }
  },
  computed: {
    heading () {
      return this.$t('general.roles')
    },
    subheading () {
      return ''
    },
    icon () {
      return 'far fa-id-card fa-fw icon-color'
    },
    rules () {
      return this.$validationRules()
    },
    headers () {
      return [
        {
          text: '#',
          align: 'center',
          sortable: true,
          value: 'id'
        },
        {
          text: this.$t('general.name'),
          align: 'center',
          value: 'name',
          sortable: true
        },
        {
          text: this.$t('general.permissions'),
          align: 'center',
          sortable: false
        },
        {
          text: this.$t('general.actions'),
          align: 'center',
          sortable: false
        }
      ]
    },
    formTitle () { // watch the dialog title
      return this.editedIndex === -1 ? this.$t('role.add') : this.$t('role.edit')
    },
    permissionsHad () { // filter the permissions that the role doesn't have
      if (this.permissions.length >= 1) {
        if (this.permissionsItem.permissions.length >= 1) {
          let permisos = Array.from(this.permissions)
          for (let i = 0; i < this.permissionsItem.permissions.length; i++) {
            const element = this.permissionsItem.permissions[i]
            let result = permisos.find(item => item.id === element.id)
            let index = permisos.indexOf(result)
            permisos.splice(index, 1)
          }
          return permisos
        } else {
          return this.permissions
        }
      }
    }
  },
  watch: {
    dialog (val) {
      val || this.close()
    }
  },
  created () {
    this.initialize()
  },
  methods: {
    changeSort (header) { // method that change the sort of the Datatable
      if (this.pagination.sortBy === header) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.descending = false
      }
      this.pagination.sortBy = header
    },
    removed (item) { // set the item that want to remove and open the dialog
      this.removedItem = item
      this.removeDialog = true
    },
    removeItem (item) { // change the status of the users to '0'
      const index = this.roles.indexOf(item)
      this.loading = true
      this.delete(item, index)
    },
    viewRol (item) {
      this.permissionsItem = item
      this.viewDialog = true
    },
    closeView () {
      this.loading = false
      this.viewDialog = false
    },
    addPermissions (item) { // set the 'type' of action that will be doing in the permissions dialog
      this.permissionTitle = 'Adding'
      this.permissionsItem = item
      this.permissionsDialog = true
    },
    savePermission (item) { // evaluate if the input have correct values and save the permission
      if (this.$refs.formPermissions.validate()) {
        this.loading = true
        this.$emit('postPermission', {item: item, index: this.roles.indexOf(item), permission: this.permission, context: this})
      }
    },
    removePermission (item) { // set the 'type' of action that will be doing in the permissions dialog
      this.permissionTitle = 'Deleting'
      this.permissionsItem = item
      this.permissionsDialog = true
    },
    deletePermission (item) { // evaluate if the input have correct values and delete the permission
      if (this.$refs.formPermissions.validate()) {
        this.loading = true
        this.$emit('deletePermission', {item: item, index: this.roles.indexOf(item), permission: this.permission, context: this})
      }
    },
    closePermission () { // close the permissions dialog and set default values
      this.permissionsDialog = false
      this.permission = ''
      this.loading = false
      this.$refs.formPermissions.resetValidation()
    },
    close () { // close the dialog and re-assing default values
      this.dialog = false
      this.editedItem = Object.assign({}, this.defaultItem)
      this.editedIndex = -1
      this.loading = false
      this.$refs.form.resetValidation()
    },
    editItem (item) { // show the element that want to edit
      this.editedIndex = this.roles.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.dialog = true
    },
    save () { // evaluate the form and save de values
      if (this.$refs.form.validate()) {
        if (this.editedIndex > -1) {
          this.loading = true
          this.put()
        } else {
          this.loading = true
          this.post()
        }
      }
    },
    initialize () { // Load example elements
      this.$emit('getRoles', { context: this })
      this.$emit('getPermissions', { context: this })
    },
    post () { // make the event to post an item
      this.$emit('postRol', { item: this.editedItem, context: this })
    },
    put () { // make the event to put (edit) an item
      this.$emit('putRol', { item: this.editedItem, index: this.editedIndex, context: this })
    },
    delete (item, index) { // make the event to delete an item
      this.$emit('deleteRol', {item: item, index: index, context: this})
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/roles.component.vue